import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import slugify from 'slugify';
import {
  format,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { NavLink, useParams, useSearchParams } from 'react-router-dom';
import { useForm, useController } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { BsCheck } from 'react-icons/bs';
import { FaFileExport } from 'react-icons/fa';
import {
  HiFlag,
  HiOutlinePencil,
} from 'react-icons/hi';
import { CASE_GET_BY_ID, SEARCH_GET_LIST } from '../../actions/types';
import {
  getCase,
  setFolderCase,
  getFolderCase,
  deleteFileCase,
  updateCase,
  moveImageToTask,
  movePictureToNumber,
} from '../../actions/case';
import { deleteData } from '../../actions';
import { uploadFileInFolder, uploadFile } from '../../actions/file';
import {
  hasAccess,
  loadOptionsCaseTasks,
  downloadFolders,
} from '../../utils';

import {
  getSearchList,
} from '../../actions/search';
import Loader from '../../components/Loader';
import ModalStandalone from '../../lib/ModalStandalone';
import ModalSendEmail from '../../components/ModalSendEmail';
import CaseForm from '../../components/form/CaseForm';
import CreateFolder from '../../components/form/CreateFolder';
import ExportFolders from '../../components/form/ExportFolders';
import FilesList from '../../components/list/FilesList';
import RootCase from './RootCase';
import NumberCase from './NumberCase';
import FolderCase from './FolderCase';
import PictureCase from './PictureCase';
import styles from './case.module.scss';

import {
  StyledSelect,
} from '../../lib/HooksFormFields';

function CheckboxSelectAll({
  checked,
  indeterminate,
  onChange,
  className,
  label,
}) {
  const inputRef = useRef(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);
  return (
    <label className={className} title="Tout sélectionner pour cette date">
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={onChange}
      />
      <span className={styles.checkboxSelectAllLabel} style={{
        fontFamily: '"Manrope-Regular", sans-serif',
        color: '#717A97',
        fontSize: '12px',
      }}>{label}</span>
    </label>
  );
}

const Case = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const modalCreateFolder = useRef();
  const modalprintFolders = useRef();
  const modalCase = useRef();
  const fileList = useRef();
  const {
    caseReducer, searchReducer, fileReducer, authReducer,
  } = useSelector((store) => store);
  const {
    isLoading: isLoadingCase, selectedCase, currentFolder,
  } = caseReducer;
  const { user } = authReducer;
  const { list, isLoading: isLoadingSearch } = searchReducer;
  const { error: errorFile, isLoading: isLoadingFile } = fileReducer;
  const [file, setFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [sendDocuments, setSendDocuments] = useState(null);
  const [optsMove, setOptsMove] = useState(null);

  const {
    control,
    reset,
  } = useForm();
  const {
    field: {
      onChange,
      value: doubleCheck,
    },
  } = useController({
    name: 'doubleCheck',
    control,
  });

  // Au niveau racine, fusionner les pictures de la case avec les files des tasks pour l'affichage
  const displayFolder = useMemo(() => {
    if (!currentFolder || !currentFolder.root || !selectedCase) return currentFolder;
    const casePictures = selectedCase.pictures || [];
    const taskPictures = (selectedCase.tasks || []).flatMap((task) => (
      (task.files || []).map((taskFile) => ({
        file: taskFile,
        number: task.number,
        taskType: task.type,
        type: task.type === 'Déchargement' || task.type === 'Retour' ? 'return' : 'departure',
      }))
    ));
    return { ...currentFolder, pictures: [...casePictures, ...taskPictures] };
  }, [currentFolder, selectedCase]);

  useEffect(() => {
    const { id } = params;
    getCase(dispatch, id);
    getSearchList(dispatch);
  }, []);

  // HANDLE GET FOLDER ACCORDING TO SEARCH PARAMS
  useEffect(() => {
    if (!selectedCase) return;
    const p = new URLSearchParams(searchParams);
    const folderId = p.get('folder');
    if (folderId) {
      getFolderCase(dispatch, folderId);
    } else {
      // IF NO ID SPECIFICIED SET FOLDER WITH selectedCase.folders
      setFolderCase(dispatch, {
        ...selectedCase,
        root: true,
      });
    }
  }, [searchParams, selectedCase]);

  // INIT VALUE DOUBLECHECK
  // INIT VALUE STATUS
  useEffect(() => {
    if (
      !list.status
      || typeof selectedCase?.doubleCheck !== 'boolean'
      || typeof doubleCheck === 'boolean'
    ) return;
    const defaultStatus = list.status?.find((opt) => opt.value === slugify(selectedCase.status || '', { lower: true }));
    reset({ doubleCheck: selectedCase?.doubleCheck, status: defaultStatus });
  }, [selectedCase?.doubleCheck, list]);

  // Options pour déplacer une image vers un numéro de document (task) d'une case
  useEffect(() => {
    const load = async () => {
      const opts = await loadOptionsCaseTasks();
      setOptsMove(opts);
    };
    load();
  }, [selectedCase]);

  // SUBMIT CASE STATUS ON CHANGE
  function handleChangeStatus(status) {
    updateCase(dispatch, {
      ...selectedCase,
      status: status.label,
    });
  }

  // SUBMIT CASE doubleCheck ON CHANGE
  function handleChangeDoubleCheck() {
    updateCase(dispatch, {
      ...selectedCase,
      doubleCheck: !doubleCheck,
    }).then(() => {
      onChange(!doubleCheck);
    });
  }

  function handleSend() {
    setSendDocuments(selectedFiles);
  }

  function handleDownload() {
    downloadFolders(currentFolder.name, selectedFiles.map((f) => ({ _id: f, type: 'file' })), selectedCase.ref);
  }

  function handleSelectionChange(selectionFiles) {
    setSelectedFiles(selectionFiles);
  }

  const addPictureCase = async (formData) => {
    const res = await uploadFile(formData);
    const pictureType = searchParams.get('pictures');
    const rawDocumentNumber = searchParams.get('piecePL') || searchParams.get('number');
    const documentNumber = rawDocumentNumber && rawDocumentNumber !== 'Sans numéro'
      ? rawDocumentNumber
      : null;
    if (res.status === 201) {
      // type: 'departure' | 'return' | 'hs'
      // taskType: 'Préparation' | 'Chargement' | 'Déchargement' | 'Retour'
      const getBaseTypeForPictureType = (t) => {
        if (t === 'hs') return 'hs';
        if (t === 'Préparation' || t === 'Chargement') return 'departure';
        if (t === 'Déchargement' || t === 'Retour') return 'return';
        return 'return';
      };

      const normalizeExistingPicture = (p) => {
        const fileId = p?.file?._id || p?.file || p?._id;
        if (!fileId) return null;
        const baseType = p?.type || getBaseTypeForPictureType(p?.taskType);
        const payload = { file: fileId, type: baseType };
        if (p?.number) payload.number = p.number;
        if (baseType !== 'hs' && p?.taskType) payload.taskType = p.taskType;
        return payload;
      };

      const newPictures = (res.data.files || []).map((f) => {
        const fileId = f?._id || f?.file?._id || f?.file;
        const baseType = getBaseTypeForPictureType(pictureType);
        const payload = { file: fileId, type: baseType };
        if (documentNumber) payload.number = documentNumber;
        if (baseType !== 'hs' && pictureType) payload.taskType = pictureType;
        return payload;
      }).filter((p) => p?.file);

      const existingPictures = (selectedCase.pictures || [])
        .map(normalizeExistingPicture)
        .filter(Boolean);

      updateCase(dispatch, {
        _id: selectedCase._id,
        pictures: [...newPictures, ...existingPictures],
      });
    }
  };

  const deletePictureCase = async (fileId) => {
    const res = await deleteData(null, `/file/${fileId}`, null, true);
    if (res.status === 200) {
      const updateFiles = selectedCase.pictures.filter((f) => f.file._id !== fileId);
      updateCase(dispatch, {
        _id: selectedCase._id,
        pictures: updateFiles,
      });
    }
  };

  async function handleChangeFile(fileValue) {
    const formData = new FormData();
    formData.append('file', fileValue);
    formData.append('title', fileValue.name);

    if (searchParams.get('pictures')) {
      addPictureCase(formData);
    } else {
      setFile(fileValue);
      await uploadFileInFolder(dispatch, formData, currentFolder);
      setFile(null);
    }
  }

  function openFolder(folderId) {
    const p = new URLSearchParams(searchParams);
    p.set('folder', folderId);
    setSearchParams(p);
  }

  function openPictureFolder(type) {
    const p = new URLSearchParams(searchParams);
    p.set('pictures', type);
    setSearchParams(p);
  }

  function openDocumentFolder(number, piecePL) {
    const p = new URLSearchParams(searchParams);
    p.set('number', number);
    if (piecePL != null && piecePL !== '') {
      p.set('piecePL', piecePL);
    } else {
      p.delete('piecePL');
    }
    setSearchParams(p);
  }

  // Normaliser un numéro pour comparaison (cohérent avec RootCase, ex. LL / LL 12345)
  function normalizeNumber(num) {
    if (num == null || num === '' || num === 'Sans numéro') return String(num ?? '');
    return String(num).trim().replace(/\s+/g, '').replace(/-/g, '');
  }

  // Helper function pour vérifier si une image correspond
  // au numéro sélectionné (ou au piecePL si fourni, ex. document lié à une LL)
  function matchesNumber(picture, selectedNumber, piecePL) {
    if (selectedNumber === 'Sans numéro') {
      return (
        !picture.number
        || picture.number === null
        || picture.number === undefined
        || picture.number === ''
        || picture.number === 'Sans numéro'
      );
    }
    const matchNumber = normalizeNumber(picture.number) === normalizeNumber(selectedNumber);
    const matchPiecePL = piecePL && normalizeNumber(picture.number) === normalizeNumber(piecePL);
    return matchNumber || matchPiecePL;
  }

  // Source de l'image : case.pictures ou task.files
  const getSourceForFile = (fileId) => {
    const inCasePicture = selectedCase?.pictures?.some(
      (p) => (p.file?._id || p.file)?.toString() === fileId?.toString(),
    );
    if (inCasePicture) {
      return { type: 'casePictures', caseId: selectedCase._id };
    }
    const task = selectedCase?.tasks?.find((t) => (
      t.files?.some((f) => (f._id || f)?.toString() === fileId?.toString())
    ));
    if (task) {
      return { type: 'task', taskId: task._id };
    }
    return null;
  };

  const handleMoveImageToTask = (fileId, targetTaskId) => {
    const source = getSourceForFile(fileId);
    if (!source) return Promise.reject(new Error('Source inconnue pour ce fichier'));
    return moveImageToTask(dispatch, fileId, targetTaskId, source).then((res) => {
      if (res?.status === 200 && selectedCase?._id) {
        getCase(dispatch, selectedCase._id);
      }
      return res;
    });
  };

  const handleMovePictureToNumber = (payload) => (
    movePictureToNumber(dispatch, payload).then((res) => {
      if (res?.status === 200 && selectedCase?._id) {
        getCase(dispatch, selectedCase._id);
      }
      return res;
    })
  );

  const renderDocument = useCallback((files, options = {}) => {
    const enableMove = options.enableMove === true;
    const groupFiles = {};
    const getDate = (f) => {
      const date = f.date ? new Date(f.date) : new Date(f.createdAt);
      date.setHours(0, 0, 0, 0); // Remet l'heure à 00:00:00.000
      return date;
    };
    (files || []).forEach((d) => {
      if (!d) return;
      const strDate = format(getDate(d), 'dd.MM.yy', { locale: fr });
      if (groupFiles[strDate]) groupFiles[strDate].push(d);
      else groupFiles[strDate] = [d];
    });
    const sortedGroup = Object.entries(groupFiles).map((d) => d[1])
      .sort((a, b) => getDate(a[0]).getTime() - getDate(b[0]).getTime());

    const toggleSelectAllForDate = (group) => {
      const groupIds = group.map((f) => f._id);
      const allSelected = groupIds.length > 0 && groupIds.every((id) => (
        selectedFiles.includes(id)
      ));
      const newSelection = allSelected
        ? selectedFiles.filter((id) => !groupIds.includes(id))
        : [...new Set([...selectedFiles, ...groupIds])];
      handleSelectionChange(newSelection);
    };

    const elements = sortedGroup.map((group) => {
      const key = format(getDate(group[0]), 'dd.MM.yy', { locale: fr });
      const groupIds = group.map((f) => f._id);
      const allSelectedForDate = groupIds.length > 0 && groupIds.every((id) => (
        selectedFiles.includes(id)
      ));
      const someSelectedForDate = groupIds.some((id) => selectedFiles.includes(id));
      return (
        <div key={`files-${key}`}>
          <div className={styles.titleListFilesRow}>
            <p className={styles.titleListFiles}>Document(s) du : {key}</p>
            <CheckboxSelectAll
              className={styles.checkboxSelectAll}
              checked={allSelectedForDate}
              indeterminate={someSelectedForDate && !allSelectedForDate}
              onChange={() => toggleSelectAllForDate(group)}
              label="Tout sélectionner"
            />
          </div>
          <div className={styles.listFiles}>
            <FilesList
              ref={fileList}
              files={group}
              selectedFiles={selectedFiles}
              handleSelectionChange={handleSelectionChange}
              initOptsMove={enableMove ? optsMove : []}
              actions={{
                print: true,
                send: (doc) => {
                  setSendDocuments(doc);
                },
                ...(enableMove && {
                  move: (dispatchParam, { _id, taskId }) => handleMoveImageToTask(_id, taskId),
                  getSourceForFile,
                  movePictureToNumber: handleMovePictureToNumber,
                }),
                delete: (f) => {
                  if (searchParams.get('pictures')) {
                    deletePictureCase(f._id);
                    return;
                  }
                  deleteFileCase(dispatch, f);
                },
              }}
            />
          </div>
        </div>
      );
    });
    return elements;
  }, [selectedCase, currentFolder?.files, optsMove, selectedFiles]);

  return (
    <>
      <ModalSendEmail
        sendDocuments={sendDocuments}
        handleClose={() => {
          setSendDocuments(null);
        }}
        handleSuccess={() => {
          fileList.current.resetSelection();
          setSendDocuments(null);
          setSelectedFiles([]);
        }}
      />
      <ModalStandalone
        ref={modalCase}
      >
        <CaseForm
          dataCase={selectedCase}
          handleClose={() => modalCase.current.close()}
        />
      </ModalStandalone>
      <ModalStandalone ref={modalCreateFolder}>
        <CreateFolder
          handleClose={() => modalCreateFolder.current.close()}
          parent={currentFolder?.root ? null : currentFolder?._id}
          caseId={selectedCase?._id}
        />
      </ModalStandalone>
      <ModalStandalone ref={modalprintFolders}>
        <ExportFolders
          folderId={currentFolder?.root ? null : currentFolder?._id}
          currentCase={selectedCase}
        />
      </ModalStandalone>
      <div className={styles.case}>
        {(isLoadingCase.includes(CASE_GET_BY_ID)
          || isLoadingSearch.includes(SEARCH_GET_LIST)
          || !selectedCase)
          ? <Loader />
          : <div className={styles.container}>
            <div className={styles.header}>
              <h1
              >
                <NavLink to={`/case/${params.id}`} >#{selectedCase.ref} {selectedCase.client}</NavLink>
                {hasAccess(user.role) && (
                  <span onClick={() => {
                    modalCase.current.open();
                  }}>
                    <HiOutlinePencil />
                  </span>
                )}
              </h1>
              <span className={styles.fields}>
                {hasAccess(user.role) && (
                  <div className={styles.checkboxField} onClick={() => handleChangeDoubleCheck()}>
                    <HiFlag />
                    <p>Double contrôle</p>
                    <div className={styles.checkbox}>
                      {doubleCheck && <BsCheck />}
                    </div>
                  </div>
                )}
                <span>
                  <div className={styles.containerSelect}>
                    <StyledSelect
                      name="status"
                      control={control}
                      options={list.status}
                      className='dark'
                      isSearchable={false}
                      isDisabled={!hasAccess(user.role)}
                      handleChange={handleChangeStatus}
                    />
                  </div>
                  <button
                    className={styles.export}
                    onClick={() => modalprintFolders.current.open()}><FaFileExport /></button>
                </span>
              </span>
              {selectedCase.name && <h3>{selectedCase.name}</h3>}
            </div>
            {/* FOLDER CASE */}
            {(currentFolder && !currentFolder?.root) && (
              <FolderCase
                currentFolder={currentFolder}
                selectedCase={selectedCase}
                searchParams={searchParams}
                file={file}
                selectedFiles={selectedFiles}
                fileList={fileList}
                errorFile={errorFile}
                isLoadingFile={isLoadingFile}
                handleChangeFile={handleChangeFile}
                handleSelectionChange={handleSelectionChange}
                handleDownload={handleDownload}
                handleSend={handleSend}
                deletePictureCase={deletePictureCase}
                renderDocument={renderDocument}
              />
            )}

            {/* ROOT CASE */}
            {!searchParams.get('pictures') && !searchParams.get('number') && currentFolder?.root && (
              <RootCase
                selectedCase={selectedCase}
                currentFolder={displayFolder}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                modalCreateFolder={modalCreateFolder}
                setSendDocuments={setSendDocuments}
                openFolder={openFolder}
                openDocumentFolder={openDocumentFolder}
              />
            )}

            {/* NUMBER CASE */}
            {!searchParams.get('pictures') && searchParams.get('number') && currentFolder?.root && (
              <NumberCase
                selectedCase={selectedCase}
                currentFolder={displayFolder}
                searchParams={searchParams}
                openPictureFolder={openPictureFolder}
              />
            )}

            {/* PICTURE CASE */}
            {currentFolder && searchParams.get('pictures') && (
              <div className={styles.folders}>
                <PictureCase
                  selectedCase={selectedCase}
                  currentFolder={displayFolder}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  selectedFiles={selectedFiles}
                  fileList={fileList}
                  optsMove={optsMove}
                  isLoadingFile={isLoadingFile}
                  handleChangeFile={handleChangeFile}
                  handleSelectionChange={handleSelectionChange}
                  handleDownload={handleDownload}
                  handleSend={handleSend}
                  getSourceForFile={getSourceForFile}
                  movePictureToNumber={handleMovePictureToNumber}
                  renderDocument={renderDocument}
                  matchesNumber={matchesNumber}
                  deletePictureCase={deletePictureCase}
                />
              </div>
            )}
          </div>
        }
      </div>
    </>
  );
};

export default Case;
